import React from "react";
import { useLocation, Navigate } from "react-router-dom";

import "../styles/OrderConfirmation.css";

const OrderConfirmation = () => {
  const location = useLocation();
  const { orderData, orderNumber } = location.state || {};
  const paymentStatus = location.state?.paymentStatus;

  // If no order data is present, redirect to home
  if (!orderData) {
    return <Navigate to="/" replace />;
  }

  // Helper function to format currency
  const formatCurrency = (amount) => `${amount} lei`;

  return (
    <div className="confirmation-container">
      <div className="confirmation-header">
        <h1 className="confirmation-title">Mulțumim pentru Comandă!</h1>
        <div className="order-number-display">
          <strong>Comanda nr. {orderNumber}</strong>
        </div>
        {paymentStatus === "success" && (
          <div className="payment-success-message">
            <p className="text-green-600 font-medium">
              Plata a fost procesată cu succes!
            </p>
          </div>
        )}
        <p className="confirmation-subtitle">
          În scurt timp veți primi un email cu toate detaliile comenzii.
        </p>
      </div>

      <div className="confirmation-content">
        {/* Order Details Section */}
        <div className="confirmation-section">
          <h2 className="section-title">Detalii Comandă</h2>

          {/* Delivery Method */}
          <div className="delivery-method">
            <h3 className="subsection-title">Metoda de livrare</h3>
            <p className="delivery-type">
              {orderData.isPickup
                ? "Ridicare de la sediu: Chișinău, Petru Rareș 37"
                : "Livrare la adresă"}
            </p>
          </div>

          {/* Recipient Information */}
          {!orderData.isPickup && (
            <div className="recipient-info">
              <h3 className="subsection-title">Informații Destinatar</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Nume:</span>
                  <span className="info-value">{orderData.recipient.name}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Telefon:</span>
                  <span className="info-value">
                    {orderData.recipient.phone}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Oraș:</span>
                  <span className="info-value">{orderData.recipient.city}</span>
                </div>
                {orderData.recipient.address && (
                  <div className="info-item">
                    <span className="info-label">Adresă:</span>
                    <span className="info-value">
                      {orderData.recipient.address}
                    </span>
                  </div>
                )}
                {orderData.recipient.message && (
                  <div className="info-item message-item">
                    <span className="info-label">Observații comandă:</span>
                    <span className="info-value">
                      {orderData.recipient.message}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Sender Information */}
          <div className="sender-info">
            <h3 className="subsection-title">Informații Expeditor</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Nume:</span>
                <span className="info-value">{orderData.sender.name}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Telefon:</span>
                <span className="info-value">{orderData.sender.phone}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Email:</span>
                <span className="info-value">{orderData.sender.email}</span>
              </div>
              {orderData.sender.address && (
                <div className="info-item">
                  <span className="info-label">Adresă:</span>
                  <span className="info-value">{orderData.sender.address}</span>
                </div>
              )}
            </div>
          </div>

          {/* Order Items */}
          <div className="order-items">
            <h3 className="subsection-title">Produse Comandate</h3>
            {orderData.orderDetails.items.map((item, index) => (
              <div key={index} className="order-item">
                <div className="item-main">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="item-image"
                  />
                  <div className="item-details">
                    <h4 className="item-name">{item.name}</h4>
                    <p className="item-quantity">Cantitate: {item.quantity}</p>
                    <p className="item-price">{formatCurrency(item.price)}</p>
                  </div>
                </div>

                {/* Extras */}
                {item.extras && item.extras.length > 0 && (
                  <div className="item-extras">
                    <h5 className="extras-title">Extra</h5>
                    {item.extras.map((extra, idx) => (
                      <div key={idx} className="extra-item-conf">
                        <img
                          src={extra.image}
                          alt={extra.name}
                          className="extra-image"
                        />
                        <span className="extra-name">{extra.name}</span>
                        <span className="extra-price">
                          {formatCurrency(extra.price)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Greeting Text */}
                {item.greetingText && (
                  <div className="greeting-text">
                    <h5 className="greeting-title">Text Felicitare</h5>
                    <p className="greeting-content">{item.greetingText}</p>
                  </div>
                )}

                {/* Delivery Details */}
                {(item.deliveryDate || item.deliveryTime) && (
                  <div className="delivery-details">
                    <h5 className="delivery-title">Detalii Livrare</h5>
                    {item.deliveryDate && (
                      <p className="delivery-date">Data: {item.deliveryDate}</p>
                    )}
                    {item.deliveryTime && (
                      <p className="delivery-time">Ora: {item.deliveryTime}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="order-summary">
            <h3 className="subsection-title">Sumar Comandă</h3>
            <div className="summary-details">
              <div className="summary-row-confirmation">
                <span className="summary-label">Subtotal:</span>
                <span className="summary-value">
                  {formatCurrency(orderData.orderDetails.subtotal)}
                </span>
              </div>
              <div className="summary-row-confirmation">
                <span className="summary-label">Cost livrare:</span>
                <span className="summary-value">
                  {formatCurrency(orderData.orderDetails.deliveryFee)}
                </span>
              </div>
              <div className="summary-row-confirmation total">
                <span className="summary-label">Total:</span>
                <span className="summary-value">
                  {formatCurrency(orderData.orderDetails.total)}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Method */}
          <div className="payment-method">
            <h3 className="subsection-title">Metodă de Plată</h3>
            <p className="payment-type">
              {orderData.payment.method === "card" && "Plată cu cardul"}
              {orderData.payment.method === "inStore" && "Achitare în magazin"}
              {orderData.payment.method === "onDelivery" &&
                "Achitare la livrare"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
